import React from 'react';
import { createPortal } from 'react-dom';
import styles from './UnifiedCardDetailModal.module.css';
import compact from './DetailModalShell.module.css';
import { I } from './icons';
import { PIcon } from '../../v2/icons';
import { useOwnsKeyboard } from '../windowKeyboard';

/**
 * Send the shell to `document.body` (2026-08-12).
 *
 * Michael: "when clicking on open project it lives inside some small sub
 * window. it should pop up all the way to the forefront for easy review. this
 * should be true for any modal — it shouldn't live inline in a sub window or
 * locked in, since a modal by its definition is a pop up window for focusing
 * on work."
 *
 * The backdrop was already `position: fixed; inset: 0; z-index: 1300`, which
 * LOOKS like it escapes — and does, until an ancestor establishes a containing
 * block. Any `transform`, `filter`, `perspective`, `contain` or `will-change`
 * on an ancestor re-parents a fixed child to THAT element's box, and modals in
 * this app animate in on `transform: scale(…)` (see `modalContentIn`). So a
 * detail modal opened FROM another modal — "View project" on a sprint card —
 * was pinned inside the opener's paper and clipped to it. `inset: 0` then
 * measured the parent, not the viewport, which is exactly the "small sub
 * window" in the report.
 *
 * A portal fixes the whole class rather than this one path: it cannot matter
 * what the calling surface is styled with if the shell isn't rendered inside
 * it. React context (theme, auth, toast) still flows through a portal, so
 * nothing else changes.
 *
 * `document.body` is read lazily rather than at module scope so this stays
 * safe under SSR/tests where `document` may not exist at import time.
 */
const ModalPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof document === 'undefined') return <>{children}</>;
  return createPortal(children, document.body);
};

/**
 * DetailModalShell — CD-20 canonical detail-modal chrome (Phase A1).
 *
 * The presentational, DATA-AGNOSTIC shell carved out of
 * `UnifiedCardDetailModal`: it owns the backdrop scrim, the flat token paper
 * (`.root`, sized `min(80vw,1600px)` / floor 1200 / `max-h calc(100vh-40px)`),
 * the top bar (back · breadcrumb slot · entity tools slot · close), the
 * focus-to-expand banner, and it hosts the 3-column body + focus/collapse
 * behaviour purely through CSS on `.root`/`.body` (the caller composes the
 * drawer | center | rail as children). Each entity (deal · project · ticket ·
 * todo) is a *composer* that fills the slots with its own body — "unify the
 * chrome, keep domain data/logic unique."
 *
 * It preserves UCDM's existing theming model exactly: a hand-rolled
 * `div.backdrop > div.root` reading global `:root` tokens (NO HubThemeScope,
 * NO MUI Dialog, NO portal). It shares `UnifiedCardDetailModal.module.css`
 * with UCDM so the extraction is a pixel-for-pixel no-op; a later cleanup
 * splits the shell classes into their own module and relocates this to
 * `components/hub/` once ticket + todo also consume it.
 *
 * CD-25 adds the second preset CD-20 named: **`compact`** — the 720px
 * single-pane the Todo container runs on (`head` · optional fixed `bands` ·
 * scrolling children · optional `footer`; no drawer, no rail, no focus mode).
 * Its classes live in the sibling `DetailModalShell.module.css` rather than
 * UCDM's, so the detail preset stays byte-identical. Ticket already consumes
 * the detail preset via `TicketCardModal`; with todo on `compact`, all four
 * CD-20 adapters are on this shell.
 *
 * BSG Deals c5 adds the third: **`deal`** — the approved Claude Design deal
 * modal (`refactor/design-sync/bsg-deals/design-live/deals/deals-modal.jsx`,
 * DISCOVERY-C5 §C). Paper `min(96vw,1780px)` on `--paper-2`, radius 6, the
 * design's 12/16 head band (`«` back · breadcrumb · entity actions · `×`),
 * and ONE scrolling body that is a three-track grid with a sticky left
 * column — `<1024` it becomes a flex column so the caller can render tabs +
 * one pane, `<768` a full-screen sheet. Like `compact`, its classes live in
 * `DetailModalShell.module.css` so `detail` stays byte-identical; it takes
 * the `detail` slots it needs (`breadcrumb`, `topbarActions`, `children`)
 * and nothing else — `focusLabel` / `tileFocused` / `rootStyle` are ignored.
 *
 * See `refactor/app-atlas/06-design-system.md` §"Detail modal shell" and the
 * CLAUDE.md component gate.
 */
export interface DetailModalShellProps {
  /**
   * CD-25 — which of the two CD-20 presets to render.
   *
   * `detail` (default) is the 3-column deal/project/ticket chrome: topbar with
   * breadcrumb + entity tools, focus-to-expand banner, `min(80vw,1600px)` paper.
   *
   * `compact` is the Todo container: a 720px single pane with **no** drawer, no
   * rail, no breadcrumb topbar and no focus mode — three fixed bands (`head` ·
   * children · `footer`) where the middle one scrolls. The two presets share
   * the scrim, the token theming (global `:root`, no HubThemeScope / MUI
   * Dialog / portal) and the escape/backdrop behaviour; they share no layout.
   *
   * `deal` (BSG Deals c5) is the approved deal modal: the design's head band
   * over ONE scrolling three-track body (a flex column `<1024`, a full-screen
   * sheet `<768`). It reads `ariaLabel` · `onClose` · `breadcrumb` ·
   * `topbarActions` · `children` (+ the opt-in `xwide`) and ignores every
   * other slot.
   */
  preset?: 'detail' | 'compact' | 'deal';
  /** compact only — the fixed top band (type tile · title · status · close).
   *  The caller composes it; the shell owns its padding and hairline. */
  head?: React.ReactNode;
  /** compact only — the fixed bottom band (ghost-left / primary-right actions).
   *  Omit for a footer-less compact pane. */
  footer?: React.ReactNode;
  /** compact only — extra fixed bands between `head` and the scrolling body
   *  (the Todo container's SOURCE and META rows). Rendered in order, outside
   *  the scroll area, so they stay put while the body scrolls. */
  bands?: React.ReactNode;
  /** Accessible dialog label (the record title, or "Create card"). */
  ariaLabel: string;
  /** Close handler — wired to backdrop click, the back button, and close. */
  onClose: () => void;
  /** Inline style forwarded to the root paper (legacy hook; usually `{}`). */
  rootStyle?: React.CSSProperties;
  /**
   * Focus-to-expand: the label of the currently-focused section/tile, or
   * `null`/`undefined` when nothing is focused. Non-null turns on focus mode
   * (the grid reshapes + the banner shows).
   */
  focusLabel?: string | null;
  /** True when a RAIL TILE (not a center section) is focused — the grid gives
   *  the rail the center column instead. */
  tileFocused?: boolean;
  /** Exit focus mode (the focus banner's "Back to overview"). */
  onExitFocus?: () => void;
  /** Breadcrumb trail rendered inside the top bar (replaces the old hardcoded
   *  `Sales › Pipeline › …`). Compose with the caller's `styles.crumbA` /
   *  `styles.crumbSep` / `styles.crumbHere`. */
  breadcrumb?: React.ReactNode;
  /** Entity-specific top-bar tools, rendered to the LEFT of the close button
   *  (e.g. the create-mode "Create card" button, per-entity actions). */
  topbarActions?: React.ReactNode;
  /**
   * The modal body — the caller composes the 3-column grid
   * (`<div className={styles.body}>drawer | main | rail</div>`) and any
   * portal overlays (composers / pickers) as trailing children.
   */
  children: React.ReactNode;
  /**
   * `deal` preset only — BSG Projects (2026-09-24): at ≥2200px the paper
   * widens to `min(96vw, 2400px)` (README §6 "drawn at 2560"), where the
   * project record columnises its centre. Opt-in and additive: the deal
   * record never sets it, so its paper (and its class list) is unchanged.
   */
  xwide?: boolean;
}

export const DetailModalShell: React.FC<DetailModalShellProps> = ({
  preset = 'detail',
  head,
  footer,
  bands,
  ariaLabel,
  onClose,
  rootStyle,
  focusLabel,
  tileFocused = false,
  onExitFocus,
  breadcrumb,
  topbarActions,
  children,
  xwide = false,
}) => {
  const focused = focusLabel != null;
  // The window on top owns the keyboard (shared/windowKeyboard.ts): focus in on
  // open, back to the opener on close. Without it a record opened from a dialog
  // left Escape with the dialog underneath.
  const keyboard = useOwnsKeyboard();

  // ── COMPACT preset (CD-25 · the Todo container) ─────────────────────
  // Deliberately its own return: the compact pane shares the scrim and the
  // theming model with `detail` but none of its layout, and threading a
  // 3-column grid through conditionals would make both harder to read.
  if (preset === 'compact') {
    return (
      <ModalPortal>
        <div
          ref={keyboard}
          tabIndex={-1}
          className={`${styles.backdrop} ${compact.ownsKeyboard}`}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          style={{ alignItems: 'center' }}
        >
          <div className={compact.compactPaper} style={rootStyle}>
            {head != null && <header className={compact.compactHead}>{head}</header>}
            {bands}
            <div className={compact.compactBody}>{children}</div>
            {footer != null && <footer className={compact.compactFoot}>{footer}</footer>}
          </div>
        </div>
      </ModalPortal>
    );
  }

  // ── DEAL preset (BSG Deals c5 · the approved deal modal) ─────────────
  // Its own return for the same reason `compact` has one: it shares the
  // portal, the scrim and the token theming with `detail`, and none of its
  // layout. The head is the design's band (deals-modal.jsx :807-820) — the
  // `«` back at 28px, the breadcrumb the caller composes, the caller's
  // actions, the `×` at 30px; the body is the one scroll port the caller
  // fills with three columns (or, `<1024`, tabs + one pane) and any in-shell
  // overlays as trailing children (see the CSS block's note on why those
  // are not clipped by the scroll port).
  if (preset === 'deal') {
    return (
      <ModalPortal>
        <div
          ref={keyboard}
          tabIndex={-1}
          className={`${styles.backdrop} ${compact.dealBackdrop} ${compact.ownsKeyboard}`}
          // Design System 2.0 (D-036, D-011): the scrim fades in over 120ms and
          // the sheet rises 10px from 98.5% over 250ms, on the shared motion
          // set (theme/motion.css); the global reduced-motion rule stops both.
          style={{ animation: 'ds-fade var(--dur-state, 120ms) ease-out both' }}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <div
            className={xwide ? `${compact.dealPaper} ${compact.dealPaperXwide}` : compact.dealPaper}
            style={{ animation: 'ds-dialog-rise var(--dur-lift, 250ms) var(--ease-out) both' }}
          >
            <header className={compact.dealHead}>
              <button
                type="button"
                className={compact.dealBack}
                onClick={onClose}
                title="Back to board"
                aria-label="Back to board"
              >
                <PIcon name="collapse" size={14} color="currentColor" />
              </button>
              <div className={compact.dealCrumb}>{breadcrumb}</div>
              <span className={compact.dealHeadSpacer} />
              {topbarActions}
              <button
                type="button"
                className={compact.dealClose}
                onClick={onClose}
                title="Close"
                aria-label="Close"
              >
                <PIcon name="close" size={14} color="currentColor" />
              </button>
            </header>
            <div className={compact.dealBody}>{children}</div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal>
    <div
      ref={keyboard}
      tabIndex={-1}
      className={`${styles.backdrop} ${compact.ownsKeyboard}`}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onMouseDown={(e) => {
        // Close on backdrop click (but not when a child intercepted it).
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`${styles.root} ${focused ? styles.focusMode : ''} ${
          tileFocused ? styles.tileFocused : ''
        }`}
        style={rootStyle}
      >
        {/* ── Top bar ────────────────────────────────────────────────── */}
        <header className={styles.topbar}>
          <div className={styles.crumbWrap}>
            <button
              type="button"
              className={styles.crumbBack}
              onClick={onClose}
              title={focused ? 'Back to overview' : 'Back to board'}
              aria-label={focused ? 'Back to overview' : 'Back to board'}
            >
              <I.Back />
            </button>
            <div className={styles.crumb}>{breadcrumb}</div>
          </div>
          <div className={styles.topbarTools}>
            {topbarActions}
            <button
              type="button"
              className={`${styles.toolBtn} ${styles.close}`}
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <I.X />
            </button>
          </div>
        </header>

        {/* ── Focus banner ───────────────────────────────────────────── */}
        {focused && (
          <div className={styles.focusBanner}>
            <button
              type="button"
              className={styles.focusBannerBack}
              onClick={onExitFocus}
            >
              <I.Back />
              Back to overview
            </button>
            <span className={styles.focusBannerTitle}>{focusLabel}</span>
            <span className={styles.focusBannerHint}>Press ESC to exit</span>
          </div>
        )}

        {children}
      </div>
    </div>
    </ModalPortal>
  );
};

export default DetailModalShell;
